/**
 * Security Settings Page
 * User-facing security controls: 2FA, password change, session management
 */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useHIPAACompliance } from '../hooks/useHIPAACompliance';
import {
  Shield, Key, Lock, Smartphone, Clock, CheckCircle, AlertTriangle,
  Eye, EyeOff, RefreshCw
} from 'lucide-react';

const SecuritySettingsPage = () => {
  const { 
    hipaaStatus, 
    enable2FA, 
    confirm2FA, 
    disable2FA, 
    changePassword,
    SESSION_TIMEOUT_MINUTES 
  } = useHIPAACompliance();
  
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [testCode, setTestCode] = useState('');
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState([]);

  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserInfo(user);
  }, []);

  const handleEnable2FA = async () => {
    setLoading(true);
    const result = await enable2FA();
    setLoading(false);
    
    if (result.success) {
      setShow2FASetup(true);
      setTestCode(result.codeForTesting || '');
      toast.success('Verification code sent to your email');
    } else {
      toast.error(result.error || 'Failed to initiate 2FA setup');
    }
  };

  const handleConfirm2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    const result = await confirm2FA(verificationCode);
    setLoading(false);
    
    if (result.success) {
      setShow2FASetup(false);
      setVerificationCode('');
      toast.success('Two-Factor Authentication enabled successfully');
    } else {
      toast.error(result.error || 'Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    const password = prompt('Enter your password to disable 2FA:');
    if (!password) return;
    
    setLoading(true);
    const result = await disable2FA(password);
    setLoading(false);
    
    if (result.success) {
      toast.success('Two-Factor Authentication disabled');
    } else {
      toast.error(result.error || 'Failed to disable 2FA');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordErrors([]);
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setLoading(true);
    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    setLoading(false);
    
    if (result.success) {
      toast.success('Password changed successfully. Please login again.');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Redirect to login
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 2000);
    } else {
      if (result.errors && result.errors.length > 0) {
        setPasswordErrors(result.errors);
      }
      toast.error(result.error || 'Failed to change password');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="security-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Security Settings
          </h1>
          <p className="text-gray-500 mt-1">Manage your account security and HIPAA compliance</p>
        </div>

        {/* Session Info */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            Session Security
          </h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Auto-Logoff Enabled</p>
                <p className="text-sm text-blue-600">
                  Your session will automatically expire after {SESSION_TIMEOUT_MINUTES} minutes of inactivity to protect patient information.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-gray-600" />
            Two-Factor Authentication (2FA)
          </h2>
          
          <div className="mb-4">
            <p className="text-gray-600 text-sm mb-3">
              Add an extra layer of security to your account. When enabled, you'll need to enter a verification code sent to your email each time you log in.
            </p>
          </div>

          {hipaaStatus.twoFactorEnabled ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">2FA is Enabled</p>
                  <p className="text-sm text-green-600">Your account has enhanced security protection.</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleDisable2FA}
                disabled={loading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="disable-2fa-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Disable 2FA
              </Button>
            </div>
          ) : show2FASetup ? (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  A verification code has been sent to your email. Enter it below to complete 2FA setup.
                </p>
                {testCode && (
                  <p className="text-xs text-yellow-600 mt-2">
                    (Test code: {testCode})
                  </p>
                )}
              </div>
              <div>
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="mt-1 max-w-xs text-center text-lg tracking-widest"
                  maxLength={6}
                  data-testid="2fa-code-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleConfirm2FA}
                  disabled={loading || verificationCode.length !== 6}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-2fa-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Verify & Enable
                </Button>
                <Button variant="outline" onClick={() => setShow2FASetup(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">2FA is Not Enabled</p>
                  <p className="text-sm text-yellow-600">
                    Enable 2FA to enhance your account security and meet HIPAA recommendations.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleEnable2FA}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="enable-2fa-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}
        </div>

        {/* Password Change */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-600" />
            Password
          </h2>

          {hipaaStatus.passwordExpired && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-800">Password Expired</p>
                <p className="text-sm text-red-600">
                  Your password has expired. Please change it to continue accessing patient information.
                </p>
              </div>
            </div>
          )}

          {hipaaStatus.passwordExpiresInDays <= 14 && !hipaaStatus.passwordExpired && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Password Expiring Soon</p>
                <p className="text-sm text-yellow-600">
                  Your password will expire in {hipaaStatus.passwordExpiresInDays} days. Consider changing it now.
                </p>
              </div>
            </div>
          )}

          {!showPasswordForm ? (
            <div>
              <p className="text-gray-600 text-sm mb-4">
                HIPAA requires passwords to be changed every 90 days. Your password must meet complexity requirements.
              </p>
              <Button 
                onClick={() => setShowPasswordForm(true)}
                variant="outline"
                data-testid="show-password-form-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {passwordErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-800 text-sm mb-1">Password Requirements Not Met:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {passwordErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <Label>Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label>New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              <div>
                <Label>Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-password-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Update Password
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordErrors([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* HIPAA Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">HIPAA Compliance Notice</p>
              <p className="text-sm text-blue-600 mt-1">
                These security settings help ensure your account meets HIPAA requirements for protecting 
                patient health information. All security events are logged and audited.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SecuritySettingsPage;
